/**
 * The environment maps a profile may be lit by — one list, both apps.
 *
 * SHARED WITH THE CUSTOMIZER. No dependencies at all.
 *
 * WHY THE LIST IS SHARED AND CURATED
 * ----------------------------------
 * The setting saved on a profile is a FILENAME, so the two apps have to agree on what each
 * name contains. They did not: `a0.hdr` was a different picture in each repo, measuring a
 * peak of 1.8 in the admin portal and 4.0 in the Customizer — the same profile lit two
 * different ways depending on who was looking.
 *
 * The files themselves are therefore synced from the admin portal, which holds the only
 * copy that counts, by the same script that vendors this module. Adding an entry here
 * means adding the file to admin-portal/public/assets/hdr and running the sync; the build
 * fails if the two sides disagree.
 *
 * WHY `peak` IS WORTH SHOWING AN OPERATOR
 * --------------------------------------
 * It is the brightest pixel in the map, and it decides whether the product gets specular
 * highlights at all. A map peaking at or below 1 has no pixel brighter than white — no
 * light source in it — so it lights everything flatly however far the exposure is raised.
 * Engraved lettering, slide serrations and stipple are read almost entirely from
 * highlights, which is why a product lit by one of those looks black and featureless next
 * to the same GLB in a general-purpose viewer.
 *
 * `a00.hdr`, the default every profile has used so far, peaks at 0.9.
 */

export interface EnvironmentMap {
  /** Filename under /assets/hdr/, identical in both apps. */
  file: string
  label: string
  /** Brightest pixel. Above ~2 the map can throw a highlight; at or below 1 it cannot. */
  peak: number
}

export const ENVIRONMENT_MAPS: EnvironmentMap[] = [
  { file: 'env_.hdr', label: 'Studio — very strong highlights', peak: 32640 },
  { file: 'venice_sunset.hdr', label: 'Sunset — strong highlights', peak: 594.8 },
  { file: 'marry_hall_1k-saturated.hdr', label: 'Hall — moderate highlights', peak: 52.2 },
  { file: 'a2.hdr', label: 'Hall (alt) — moderate highlights', peak: 52.2 },
  { file: 'a0.hdr', label: 'Soft studio — clear highlights', peak: 4 },
  { file: 'marry12.hdr', label: 'Soft room — slight highlights', peak: 2.9 },
  { file: 'marry11.hdr', label: 'Soft room (dimmer) — slight highlights', peak: 2.3 },
  { file: 'marry10.hdr', label: 'Bright flat — no highlights', peak: 1.9 },
  { file: 'a00.hdr', label: 'Default — flat, no highlights', peak: 0.9 },
  { file: 't1.hdr', label: 'Neutral flat — no highlights', peak: 0.9 },
  { file: 'm2.hdr', label: 'Dark flat — no highlights', peak: 1 },
]

/**
 * Where the maps are served from.
 *
 * Defaults to each app's own public folder, which is where they live today. Point it at a
 * bucket and the files stop needing to ship with either deploy — they are ~1.5 MB each and
 * come to 17 MB in the admin portal and 76 MB in the Customizer, almost none of which any
 * one profile uses.
 *
 * Only the map a profile actually names is ever fetched, so this changes where one file
 * comes from rather than how many are loaded.
 */
let baseUrl = '/assets/hdr/'

/** Call once at startup, from whatever holds the app's configuration. */
export function setEnvironmentBaseUrl(url?: string | null): void {
  if (!url) return
  baseUrl = url.endsWith('/') ? url : `${url}/`
}

export function environmentUrl(file: string): string {
  return `${baseUrl}${file}`
}

/**
 * What a NEW profile starts with.
 *
 * Deliberately not the same as DEFAULT_ENVIRONMENT. This one is a choice about how new work
 * should look; that one is a statement of fact about what existing profiles are already
 * lit by, and changing it would silently relight every product in the catalogue.
 */
export const NEW_PROFILE_ENVIRONMENT = 'a0.hdr'

/** What every profile was lit by before this setting existed. */
export const DEFAULT_ENVIRONMENT = 'a00.hdr'

/**
 * Read the environment filename off `env_param`.
 *
 * The admin stores a Select as `{ label, value }` while anything saved before this setting
 * existed has no key at all, so both shapes and absence have to resolve — absence to the
 * map every profile has been using, or changing nothing would change everything.
 *
 * An unrecognised name falls back too: the file may exist in one app and not the other, and
 * a missing environment renders the product unlit rather than merely different.
 */
export function resolveEnvironmentMap(envParam: any): string {
  const raw = envParam?.environmentMap
  const value = raw && typeof raw === 'object' ? raw.value : raw
  if (!value || typeof value !== 'string') return DEFAULT_ENVIRONMENT
  return ENVIRONMENT_MAPS.some((e) => e.file === value) ? value : DEFAULT_ENVIRONMENT
}
